import React, { useRef, useMemo, useState, useEffect } from 'react';
import LogoTicker from './LogoTicker';
import OptimizedImage from './OptimizedImage';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ChevronDown, Menu, X, MessageCircle, Instagram, Facebook, ArrowRight, Phone } from 'lucide-react';
import { gsap } from 'gsap';
import { useLanguage, translations } from '../contexts/language';
import { useLanguageFont } from '../hooks/useLanguageFont';
import SEO from './SEO';

// 3D Tapioca Background Components
function TapiocaField({ count = 40 }) {
  const meshRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const base = useMemo(() => {
    const positions = [];
    const speeds = [];
    const phases = [];
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 10;
      const y = Math.random() * 6 - 2;
      const z = - (Math.random() * 8 + 2);
      positions.push(new THREE.Vector3(x, y, z));
      speeds.push(0.1 + Math.random() * 0.3);
      phases.push(Math.random() * Math.PI * 2);
    }
    return { positions, speeds, phases };
  }, [count]);

  useEffect(() => {
    if (!meshRef.current) return;
    for (let i = 0; i < count; i++) {
      const p = base.positions[i];
      dummy.position.copy(p);
      dummy.scale.setScalar(0.4 + Math.random() * 0.4);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [base, count, dummy]);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.getElapsedTime();
    for (let i = 0; i < count; i++) {
      const basePos = base.positions[i];
      const speed = base.speeds[i];
      const phase = base.phases[i];
      const y = basePos.y + Math.sin(t * speed + phase) * 0.5;
      const x = basePos.x + Math.cos(t * (speed * 0.5) + phase) * 0.3;
      const rot = t * 0.1 + phase;
      dummy.position.set(x, y, basePos.z);
      dummy.rotation.set(rot * 0.2, rot * 0.4, rot * 0.1);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
      <sphereGeometry args={[0.24, 32, 32]} />
      <meshPhysicalMaterial
        color="#1a1a1a"
        roughness={0.2}
        metalness={0.1}
        clearcoat={0.8}
        clearcoatRoughness={0.1}
        transparent
        opacity={0.6}
        transmission={0.2}
        thickness={0.5}
      />
    </instancedMesh>
  );
}

function TapiocaBackground({ isMobile }) {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
      <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0, 4], fov: 50 }} gl={{ alpha: true, powerPreference: "high-performance" }}>
        <ambientLight intensity={0.8} />
        <directionalLight position={[5, 5, 5]} intensity={0.5} />
        <directionalLight position={[-5, -5, -5]} intensity={0.2} />
        <fog attach="fog" args={["#fdfbf7", 4, 12]} />
        <TapiocaField count={isMobile ? 15 : 35} />
      </Canvas>
    </div>
  );
}

// Custom Flag Components
function USFlag() {
  return <img src="https://flagcdn.com/w40/us.png" srcSet="https://flagcdn.com/w80/us.png 2x" width="24" height="16" className="rounded-[2px] border border-gray-300" alt="US" loading="lazy" />;
}
function IraqFlag() {
  return <img src="https://flagcdn.com/w40/iq.png" srcSet="https://flagcdn.com/w80/iq.png 2x" width="24" height="16" className="rounded-[2px] border border-gray-300" alt="Iraq" loading="lazy" />;
}
function KurdistanFlag() {
  return <img src={process.env.PUBLIC_URL + "/media/kurdistanflag.png"} width="24" height="16" className="rounded-[2px] border border-gray-300" alt="Kurdistan" loading="lazy" />;
}

// Language Picker
function LanguagePicker({ currentLang, setCurrentLang }) {
  const [isOpen, setIsOpen] = useState(false);
  const languages = [
    { code: 'en', name: 'English', flag: <USFlag /> },
    { code: 'ku', name: 'کوردی', flag: <KurdistanFlag /> },
    { code: 'ar', name: 'العربية', flag: <IraqFlag /> }
  ];
  const selectedLanguage = languages.find(lang => lang.code === currentLang);
  const isRtl = currentLang === 'ar' || currentLang === 'ku';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 rounded-full border border-gray-200 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-gray-50 transition-colors shadow-md group"
      >
        <span className="flex items-center justify-center transform group-hover:scale-110 transition-transform">
          {selectedLanguage.flag}
        </span>
      </button>

      {isOpen && (
        <div className={`absolute top-full mt-4 ${isRtl ? 'left-0' : 'right-0'} w-40 bg-white/90 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden py-2 animate-in fade-in zoom-in-95 duration-200`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => { setCurrentLang(lang.code); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[hsl(318.46deg_26%_39.22%)]/10 transition-all duration-200 ${currentLang === lang.code ? 'bg-[hsl(318.46deg_26%_39.22%)]/10 text-[hsl(318.46deg_26%_39.22%)]' : 'text-gray-600'}`}
            >
              <span className="text-lg flex items-center shadow-sm rounded-sm overflow-hidden">{lang.flag}</span>
              <span className={`font-medium text-sm ${currentLang === lang.code ? 'font-bold' : ''}`}>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Liquid Menu
function LiquidMenu({ isOpen, setIsOpen, currentLang }) {
  const menuRef = useRef();
  const overlayRef = useRef();
  const liquidRef = useRef();
  const itemsRef = useRef([]);
  const isRtl = currentLang === 'ar' || currentLang === 'ku';

  useEffect(() => {
    if (isOpen) {
      gsap.set(menuRef.current, { display: 'block' });
      gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: "power2.out" });
      gsap.fromTo(liquidRef.current,
        { x: isRtl ? '-100%' : '100%', skewX: isRtl ? -10 : 10 },
        { x: '0%', skewX: 0, duration: 0.8, ease: "power3.out" }
      );
      gsap.fromTo(itemsRef.current,
        { x: isRtl ? -50 : 50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6, stagger: 0.1, delay: 0.3, ease: "power2.out" }
      );
    } else {
      gsap.to(itemsRef.current, { x: isRtl ? -50 : 50, opacity: 0, duration: 0.3, stagger: 0.05 });
      gsap.to(liquidRef.current, { x: isRtl ? '-100%' : '100%', skewX: isRtl ? 5 : -5, duration: 0.6, ease: "power3.in", delay: 0.1 });
      gsap.to(overlayRef.current, { opacity: 0, duration: 0.3, delay: 0.4, onComplete: () => gsap.set(menuRef.current, { display: 'none' }) });
    }
  }, [isOpen, isRtl]);

  const t = translations[currentLang];

  return (
    <div ref={menuRef} className="fixed inset-0 z-[60] hidden">
      <div ref={overlayRef} className="absolute inset-0 bg-black/10 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
      <div ref={liquidRef} className={`absolute top-0 ${isRtl ? 'left-0 border-r' : 'right-0 border-l'} w-full md:w-[480px] h-full bg-gradient-to-br from-orange-50/95 via-white/95 to-pink-50/95 backdrop-blur-2xl border-white/20 shadow-2xl`}>
        <button onClick={() => setIsOpen(false)} className={`absolute top-8 ${isRtl ? 'left-8' : 'right-8'} w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-all duration-300`}>
          <X className="w-6 h-6 text-gray-800" />
        </button>
        <div className="h-full flex flex-col justify-center px-12 md:px-20" dir={isRtl ? 'rtl' : 'ltr'}>
          {/* Menu Logos */}
          {/* Menu Logos */}
          <div ref={el => itemsRef.current[0] = el} className="flex items-center gap-4 mb-12 opacity-0">
            <img
              src={process.env.PUBLIC_URL + "/media/ibubble.png"}
              alt="ibubble logo"
              className="h-12 w-auto object-contain"
            />
            <div className="h-8 w-px bg-gray-300 mx-2"></div>
            <img
              src={process.env.PUBLIC_URL + "/media/logo.png"}
              alt="bobatea logo"
              className="h-14 w-auto object-contain"
            />
          </div>

          <nav className="space-y-8">
            {Object.entries(t.menu).map(([key, label], index) => (
              <div key={key} ref={el => itemsRef.current[index + 1] = el} className="overflow-hidden">
                <a
                  href={key === 'products' ? '#products' : key === 'about' ? '#about' : key === 'contact' ? '#footer' : '#'}
                  onClick={() => setIsOpen(false)}
                  className="block text-3xl md:text-4xl font-bold text-gray-900 hover:text-[hsl(318.46deg_26%_39.22%)] transition-colors duration-300 font-outfit tracking-tight"
                >
                  {label}
                </a>
              </div>
            ))}
          </nav>
          <div ref={el => itemsRef.current[6] = el} className="mt-16 pt-8 border-t border-gray-200 opacity-0">
            <div className="flex gap-6">
              <a href="https://www.instagram.com/ibubble.iq/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-orange-500 transition-colors"><Instagram className="w-6 h-6" /></a>
              <a href="https://www.facebook.com/Ibubble.IQ/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-600 transition-colors"><Facebook className="w-6 h-6" /></a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hero Language Switcher
function HeroLanguageSwitcher({ currentLang, setCurrentLang }) {
  const languages = [
    { code: 'en', label: 'English', flag: <USFlag /> },
    { code: 'ku', label: 'کوردی', flag: <KurdistanFlag /> },
    { code: 'ar', label: 'العربية', flag: <IraqFlag /> }
  ];

  return (
    <div className="inline-flex items-center gap-1 p-1 bg-white/40 backdrop-blur-md rounded-full border border-white/50 mb-8 shadow-sm hover:bg-white/50 transition-colors duration-300">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => setCurrentLang(lang.code)}
          className={`
            px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2
            ${currentLang === lang.code
              ? 'bg-white text-gray-900 shadow-sm scale-105'
              : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
            }
          `}
        >
          <span className="w-5">{lang.flag}</span>
          <span className="hidden md:inline">{lang.label}</span>
          <span className="md:hidden">{lang.code.toUpperCase()}</span>
        </button>
      ))}
    </div>
  );
}

// Main Hero Component
const Hero3D = () => {
  const { currentLang, setCurrentLang, isTransitioning } = useLanguage();
  const { fontClass } = useLanguageFont();
  const isRtl = currentLang === 'ar' || currentLang === 'ku';
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [scrolled, setScrolled] = useState(false);

  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const buttonsRef = useRef(null);
  const handRef = useRef(null);

  const logoRef = useRef(null);
  const langSwitcherRef = useRef(null);

  const t = translations[currentLang];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Entrance Animation
  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(logoRef.current, { y: -50, opacity: 0 }, { y: 0, opacity: 1, duration: 1 })
      .fromTo(langSwitcherRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 }, "-=0.8")
      .fromTo(titleRef.current,
        { y: 100, opacity: 0, rotateX: 20 },
        { y: 0, opacity: 1, rotateX: 0, duration: 1.2 }, "-=0.5")
      .fromTo(subtitleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 }, "-=0.8")
      .fromTo(descRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 }, "-=0.8")
      .fromTo(buttonsRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 }, "-=0.6")
      .fromTo(handRef.current,
        { x: 200, opacity: 0, rotate: 10 },
        { x: 0, opacity: 1, rotate: -12, duration: 1.5, ease: "power2.out" }, "-=1");
  }, []);

  // Magnetic Button Effect
  const handleMouseMove = (e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    gsap.to(btn, { x: x * 0.2, y: y * 0.2, duration: 0.3, ease: "power2.out" });
  };

  const handleMouseLeave = (e) => {
    gsap.to(e.currentTarget, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.5)" });
  };

  return (
    <div
      className={`relative w-full min-h-screen overflow-hidden bg-[#f8f5f2] transition-opacity duration-300 ease-in-out ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SEO
        title={t.title.replace('\n', ' ')}
        description={t.description}
        keywords={
          currentLang === 'ku' ? 'بەبڵتی, دابینکەر, کوردستان, عێراق, سلێمانی, هەولێر' :
            currentLang === 'ar' ? 'بابل تي, مورد, العراق, كردستان, أربيل, السليمانية, تدريب' :
              'Bubble Tea Supplier, Boba, Kurdistan, Iraq, Erbil, Sulaymaniyah, Training, Wholesale'
        }
        lang={currentLang}
      />

      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-white to-pink-50/30" />
      <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-orange-200/20 rounded-full blur-[120px] mix-blend-multiply animate-pulse" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-pink-200/20 rounded-full blur-[100px] mix-blend-multiply" />

      <TapiocaBackground isMobile={isMobile} />

      {/* Navigation */}
      <nav className={`absolute top-0 left-0 right-0 z-40 px-6 py-6 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-md shadow-sm py-4' : ''}`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div ref={logoRef} className="flex items-center gap-4">
            <img
              src={process.env.PUBLIC_URL + "/media/ibubble.png"}
              alt="ibubble logo"
              className="h-16 w-auto object-contain"
            />
            <div className="h-8 w-px bg-gray-300 mx-2"></div>
            <img
              src={process.env.PUBLIC_URL + "/media/logo.png"}
              alt="bobatea logo"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </div>
        </div>
      </nav>

      {/* Sticky Menu Icon */}
      <div className={`fixed top-6 z-50 ${isRtl ? 'left-6' : 'right-6'}`}>
        <button
          onClick={() => setMenuOpen(true)}
          className="w-12 h-12 rounded-full border border-gray-200 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-gray-50 transition-colors shadow-md group"
        >
          <Menu className="w-6 h-6 text-gray-700 group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Sticky Language Picker */}
      <div className={`fixed top-6 z-50 ${isRtl ? 'left-20' : 'right-20'}`}>
        <LanguagePicker currentLang={currentLang} setCurrentLang={setCurrentLang} />
      </div>

      {/* Main Content */}
      <div
        className="relative z-10 max-w-7xl mx-auto px-6 pt-44 md:pt-40 pb-12 md:pb-20 min-h-[auto] md:min-h-screen flex flex-col md:flex-row items-center"
        style={{ zoom: isMobile ? 0.9 : 1 }}
      >
        {/* Text Content */}
        <div className="w-full md:w-1/2 z-20" dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="relative">
            <div ref={langSwitcherRef} className="hidden md:flex justify-start">
              <HeroLanguageSwitcher currentLang={currentLang} setCurrentLang={setCurrentLang} />
            </div>

            <h1 ref={titleRef} className={`text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.1] tracking-tighter mb-2 whitespace-pre-line ${fontClass}`}>
              {t.title}
            </h1>

            <div ref={subtitleRef} className="overflow-hidden mb-4">
              <p className={`text-2xl md:text-3xl font-medium text-[hsl(318.46deg_26%_39.22%)] ${currentLang === 'en' ? 'font-caveat' : (currentLang === 'ar' || currentLang === 'ku') ? 'font-harmattan' : 'font-playpen'}`}>
                {t.subtitle}
              </p>
            </div>

            <div ref={descRef} className="max-w-md mb-6 md:mb-8">
              <p className={`text-lg text-gray-600 leading-relaxed ${fontClass}`}>
                {t.description}
              </p>
            </div>

            <div ref={buttonsRef} className="flex flex-wrap gap-4">
              <a
                href="#products"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                className="btn-magnetic px-8 py-4 bg-gray-900 text-white rounded-full font-semibold text-lg flex items-center gap-2 hover:shadow-xl hover:shadow-gray-900/20 transition-all"
              >
                {t.products}
                <ArrowRight className={`w-5 h-5 ${isRtl ? 'rotate-180' : ''}`} />
              </a>

              <a
                href="https://wa.me/9647701542782"
                target="_blank"
                rel="noopener noreferrer"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                className="btn-magnetic px-8 py-4 bg-white text-gray-900 border border-gray-200 rounded-full font-semibold text-lg flex items-center gap-2 hover:border-green-500 hover:text-green-600 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                {t.whatsapp}
              </a>
            </div>
          </div>
        </div>

        {/* Hero Image / 3D Element */}
        <div className={`w-full md:w-1/2 relative h-[40vh] md:h-auto flex items-center justify-center md:justify-end mt-8 md:mt-0`}>
          <div ref={handRef} className="relative w-full max-w-[600px] aspect-square">
            {/* Decorative Circle */}
            <div className="absolute inset-0 border border-gray-200 rounded-full scale-90 animate-spin-slow opacity-50" style={{ animationDuration: '30s' }} />
            <div className="absolute inset-0 border border-dashed border-gray-300 rounded-full scale-75 animate-spin-slow opacity-30" style={{ animationDuration: '40s', animationDirection: 'reverse' }} />

            <OptimizedImage
              src={process.env.PUBLIC_URL + "/media/hand_ibubbleIQ_bobatea.webp"}
              alt="Hand holding bubble tea"
              className="w-full h-full object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-700"
              priority={true}
            />
          </div>
        </div>
      </div>

      {/* Liquid Menu Overlay */}
      <LiquidMenu isOpen={menuOpen} setIsOpen={setMenuOpen} currentLang={currentLang} />


    </div>

  );
};

export default Hero3D;